import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkoutFeed } from "../../hooks/useWorkoutFeed";
import WorkoutPostCard from "../WorkoutPostCard/WorkoutPostCard";
import WorkoutPostCardSkeleton from "../WorkoutPostCard/WorkoutPostCardSkeleton";
import FeedEmptyState from "./FeedEmptyState";
import FeedErrorState from "./FeedErrorState";
import "./WorkoutFeed.css";

const WorkoutFeed = () => {
  const navigate = useNavigate();
  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useWorkoutFeed();

  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!hasNextPage) return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isError) {
    return <FeedErrorState onRetry={refetch} />;
  }

  if (isLoading) {
    return (
      <div className="workout-feed">
        {Array.from({ length: 3 }).map((_, i) => (
          <WorkoutPostCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  const posts = data?.pages.flatMap((page) => page.posts) ?? [];

  if (posts.length === 0) {
    return <FeedEmptyState onFindFriends={() => navigate("/profile")} />;
  }

  return (
    <div className="workout-feed">
      {posts.map((post) => (
        <WorkoutPostCard key={post.id} post={post} />
      ))}

      {isFetchingNextPage && <WorkoutPostCardSkeleton />}
      <div ref={sentinelRef} className="workout-feed-sentinel" aria-hidden="true" />

      {!hasNextPage && posts.length > 0 && (
        <p className="workout-feed-end">You're all caught up 🎉</p>
      )}
    </div>
  );
};

export default WorkoutFeed;
